/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import "./App.scss";
import { AgentProvider, useAgentContext } from "./contexts/AgentContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Altair } from "./components/altair/Altair";
import ControlTray, { ControlTrayHandle } from "./components/control-tray/ControlTray";
import { ApiLoader, GlobalApiLoadingOverlay } from "./components/api-loader";
import SplashScreen from "./components/splash-screen/SplashScreen";
import LoginScreen from "./components/login/LoginScreen";
import { RegisterScreen } from "./components/register";
import WelcomeMessage from "./components/welcome-message/WelcomeMessage";
import { AllPermissions } from "./utils/permissions";
import { DraftProfile, getStepLabel } from "./services/draftStorageService";
import cn from "classnames";
import { isAuthenticated, getUserData, clearAllAuth, getSavedCredentials, saveSession, getSavedPreferences, savePreferences } from "./utils/session";
import { login } from "./utils/api";
import { getAllPermissionsStatus } from "./utils/permissions";
import { getRuntimeConfig } from "./config/runtime-config";
import { initCapacitorShell } from "./utils/capacitorShell";
import { isNative } from "./utils/cameraPreviewHelper";

// Get runtime configuration
const config = getRuntimeConfig();

// Log configuration (for debugging)
console.log("🔧 [APP] Runtime Config loaded:", {
  backendUrl: config.BACKEND_URL,
  apiBaseUrl: config.API_BASE_URL,
  hasGeminiKey: !!config.GEMINI_API_KEY
});

// NOTE: Gemini API Key is now optional in multi-agent mode
// The system uses ephemeral tokens from the Python backend
if (config.BACKEND_URL) {
  console.log("🚀 [APP] Multi-agent mode enabled - using ephemeral tokens from:", config.BACKEND_URL);
} else {
  console.warn("⚠️ [APP] REACT_APP_BACKEND_URL not set - agent orchestration may not work correctly");
}

function CharacterCircularPulse({
  active,
  volume,
}: {
  active: boolean;
  volume: number;
}) {
  const pulse = active ? Math.min(1, Math.max(0, volume * 6)) : 0;

  return (
    <div
      className="streaming-console__character-audio-ring"
      aria-hidden
      style={{ ["--character-pulse" as any]: pulse } as React.CSSProperties}
    >
      <span className="streaming-console__character-audio-circle streaming-console__character-audio-circle--one" />
      <span className="streaming-console__character-audio-circle streaming-console__character-audio-circle--two" />
      <span className="streaming-console__character-audio-circle streaming-console__character-audio-circle--three" />
    </div>
  );
}

// Main app component that handles the Agent session
function MainApp({
  userData,
  permissions,
  voiceGender,
  resumeDraft,
  onNavigateHome,
}: {
  userData: { username: string; fullName: string; mobileNumber: string } | null;
  permissions: AllPermissions | null;
  voiceGender: 'male' | 'female';
  resumeDraft?: DraftProfile;
  onNavigateHome: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const {
    disconnect,
    connected,
    connecting,
    volume,
    routerAssistantSubtitle,
    flowStep,
    currentAgentId,
    companies,
    loading,
  } = useAgentContext();

  const packageSelectMode = useMemo(
    () =>
      flowStep === "package_selection" &&
      currentAgentId === "policy_agent" &&
      companies.length > 0 &&
      !loading &&
      !connecting,
    [flowStep, currentAgentId, companies.length, loading, connecting],
  );
  const popupCardMode = useMemo(
    () =>
      Boolean(
        flowStep &&
          (flowStep.startsWith("underwriting_") ||
            flowStep === "policy_survey_options" ||
            flowStep === "vehicle_survey_options" ||
            flowStep === "claim_survey_options" ||
            flowStep === "claim_policy_inquiry" ||
            flowStep === "claim_workshop_options" ||
            flowStep === "claim_contracted_workshops" ||
            flowStep === "claim_non_contracted_form" ||
            flowStep === "claim_number_display" ||
            flowStep === "policy_confirmation" ||
            flowStep === "policy_vehicle_upload" ||
            flowStep === "vehicle_upload" ||
            flowStep === "claim_vehicle_upload"),
      ),
    [flowStep],
  );
  const hasActiveFlowContent = useMemo(
    () => Boolean(flowStep && flowStep !== "idle"),
    [flowStep],
  );
  const compactTopMode = packageSelectMode || popupCardMode || hasActiveFlowContent;

  const effectiveAssistantSubtitle = useMemo(() => {
    if (routerAssistantSubtitle === "policy" || routerAssistantSubtitle === "claim") {
      return routerAssistantSubtitle;
    }
    if (currentAgentId === "policy_agent") return "policy" as const;
    if (currentAgentId === "claim_agent") return "claim" as const;
    if (currentAgentId?.includes("claim") || flowStep?.startsWith("claim_")) {
      return "claim" as const;
    }
    if (flowStep && flowStep !== "idle") {
      return "policy" as const;
    }
    return null;
  }, [routerAssistantSubtitle, currentAgentId, flowStep]);

  const [packagePanelVisible, setPackagePanelVisible] = useState(false);
  const isSurveyActive =
    flowStep === "vehicle_survey" || flowStep === "claim_vehicle_survey";

  const shouldShowGlobalStatus = useMemo(() => {
    if (isSurveyActive) return false;
    return Boolean(flowStep && flowStep !== "idle");
  }, [flowStep, isSurveyActive]);

  const globalStatusText = useMemo(
    () => getStepLabel(currentAgentId || "policy_agent", flowStep || "idle"),
    [currentAgentId, flowStep],
  );

  useEffect(() => {
    if (!packageSelectMode) {
      setPackagePanelVisible(false);
      return;
    }
    setPackagePanelVisible(false);
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const delayMs = prefersReduced ? 0 : 520;
    const t = window.setTimeout(() => setPackagePanelVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [packageSelectMode]);

  // Camera controls for vehicle survey
  const [cameraControls, setCameraControls] = useState<ControlTrayHandle | null>(null);

  const handleGoHome = async () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    disconnect();
    onNavigateHome();
  };

  // Camera control callbacks for Altair (vehicle survey)
  const handleCameraControlsReady = useCallback((controls: ControlTrayHandle) => {
    console.log("📹 [App] Camera controls received from ControlTray");
    setCameraControls(controls);
  }, []);

  const handleRequestStartCamera = useCallback(async (): Promise<boolean> => {
    if (cameraControls) {
      return await cameraControls.startCamera();
    }
    console.warn("⚠️ [App] Camera controls not available");
    return false;
  }, [cameraControls]);

  const handleRequestCaptureFrame = useCallback(async (): Promise<string | null> => {
    if (cameraControls) {
      return await cameraControls.captureFrame();
    }
    console.warn("⚠️ [App] Camera controls not available");
    return null;
  }, [cameraControls]);

  const handleRequestStopCamera = useCallback(() => {
    if (cameraControls) {
      cameraControls.stopCamera();
    }
  }, [cameraControls]);

  return (
    <div
      className={cn("streaming-console", {
        "streaming-console--package-select": compactTopMode,
        "streaming-console--package-selection-page": packageSelectMode,
      })}
    >
      <main>
        <div className="streaming-console__bg" aria-hidden />
        <div className="streaming-console__moving" aria-hidden />
        <div className="streaming-console__brand">
          {compactTopMode ? (
            <div
              className="streaming-console__package-top-row"
              dir="rtl"
              aria-live="polite"
            >
              <div className="streaming-console__package-hero">
                {packageSelectMode && (
                  <img
                    className="streaming-console__package-page-brand-logo"
                    src="/new/blue.png"
                    alt=""
                    aria-hidden
                  />
                )}
                <div className="streaming-console__brand-cluster streaming-console__brand-cluster--package-select">
                  <div className="streaming-console__character-pulse">
                    <CharacterCircularPulse
                      active={connected}
                      volume={volume}
                    />
                    <div className="streaming-console__character-card">
                      <img
                        className="streaming-console__character-img"
                        src="/new/Character.png"
                        alt=""
                      />
                    </div>
                  </div>
                  {effectiveAssistantSubtitle && (
                    <p
                      className="streaming-console__router-assistant-caption streaming-console__router-assistant-caption--package-select"
                      dir="rtl"
                    >
                 
                      <span className="streaming-console__router-assistant-caption-emphasis">
                        {effectiveAssistantSubtitle === "policy"
                          ? "انشاء وثيقة تأمين"
                          : "فتح مطالبة"}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              {shouldShowGlobalStatus ? (
                <div className="streaming-console__package-status" dir="rtl">
                  <div className="streaming-console__package-status-label-row">
                    <span className="streaming-console__package-status-dot" aria-hidden />
                    <span className="streaming-console__package-status-label">الحاله</span>
                  </div>
                  <div className="streaming-console__package-status-value">{globalStatusText}</div>
                </div>
              ) : (
                <div className="streaming-console__package-status-placeholder" aria-hidden />
              )}
            </div>
          ) : (
            <div className="streaming-console__brand-center">
              <img
                className="streaming-console__brand-icon"
                src="/new/blue.png"
                alt="أمين"
              />
              <div className="streaming-console__brand-cluster">
                <div className="streaming-console__character-pulse">
                  <CharacterCircularPulse
                    active={connected}
                    volume={volume}
                  />
                  <div className="streaming-console__character-card">
                    <img
                      className="streaming-console__character-img"
                      src="/new/Character.png"
                      alt=""
                    />
                  </div>
                </div>
                {effectiveAssistantSubtitle && (
                  <p
                    className="streaming-console__router-assistant-caption"
                    dir="rtl"
                  >
                    <span className="streaming-console__router-assistant-caption-prefix">
                      المساعده في{" "}
                    </span>
                    <span className="streaming-console__router-assistant-caption-emphasis">
                      {effectiveAssistantSubtitle === "policy"
                        ? "انشاء وثيقة تأمين"
                        : "فتح مطالبة"}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <ApiLoader />
        {shouldShowGlobalStatus && !compactTopMode && (
          <div className="streaming-console__global-status" dir="rtl">
            <div className="streaming-console__global-status-label-row">
              <span className="streaming-console__global-status-dot" aria-hidden />
              <span className="streaming-console__global-status-label">الحاله</span>
            </div>
            <div className="streaming-console__global-status-value">{globalStatusText}</div>
          </div>
        )}
        <div className="main-app-area">
          {/* APP goes here */}
          <Altair
            key={userData?.username || 'no-user'}
            permissions={permissions}
            userData={userData}
            voiceGender={voiceGender}
            onRequestStartCamera={handleRequestStartCamera}
            onRequestCaptureFrame={handleRequestCaptureFrame}
            onRequestStopCamera={handleRequestStopCamera}
          />
        </div>

        {/* Video element lives outside main-app-area so its fixed positioning
            isn't trapped inside main-app-area's stacking context (z-index: 3).
            On native, CameraPreview provides its own camera layer, so the <video>
            element is only needed for the web getUserMedia path. */}
        {!isNative() && (
          <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
              "stream--survey-fullscreen":
                flowStep === "vehicle_survey" || flowStep === "claim_vehicle_survey",
            })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        )}

        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
          onCameraControlsReady={handleCameraControlsReady}
          onGoHome={handleGoHome}
          forceCollapse={compactTopMode}
        />
      </main>
    </div>
  );
}

function App() {
  // Splash screen state
  const [showSplash, setShowSplash] = useState<boolean>(true);
  // Login screen state
  const [showLogin, setShowLogin] = useState<boolean>(false);
  // Register screen state
  const [showRegister, setShowRegister] = useState<boolean>(false);
  /** Increment when opening register so the register form remounts with empty fields. */
  const [registerScreenKey, setRegisterScreenKey] = useState(0);
  // Welcome message state
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  // Permissions state
  const [permissions, setPermissions] = useState<AllPermissions | null>(null);
  // User data state
  const [userData, setUserData] = useState<{ username: string; fullName: string; mobileNumber: string } | null>(null);
  // Voice gender preference
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('male');
  // User location state
  const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | undefined>(undefined);
  // Resume draft (when user selects a saved draft from WelcomeMessage)
  const [resumeDraft, setResumeDraft] = useState<DraftProfile | undefined>(undefined);
  // True while attempting a silent auto-login in the background
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);

  useEffect(() => {
    void initCapacitorShell();
  }, []);

  useEffect(() => {
    // Check if user is already authenticated via session (localStorage persists across restarts)
    if (isAuthenticated()) {
      const userDataFromSession = getUserData();
      if (userDataFromSession) {
        setUserData(userDataFromSession);
      } else {
        clearAllAuth();
      }
    }
  }, []);

  // Attempt silent auto-login with stored credentials and return true on success
  const tryAutoLogin = async (): Promise<boolean> => {
    const saved = getSavedCredentials();
    if (!saved) return false;
    setIsAutoLoggingIn(true);
    try {
      const response = await login({ mobile: saved.mobile, password: saved.password });
      saveSession({
        token: response.token,
        userName: response.userName,
        fullName: response.fullName || '',
        mobile: response.mobile,
        email: response.email,
      });
      setUserData({
        username: response.userName,
        fullName: response.fullName || '',
        mobileNumber: response.mobile,
      });
      console.log('✅ [App] Auto-login successful');
      return true;
    } catch (err) {
      console.warn('⚠️ [App] Auto-login failed:', err);
      return false;
    } finally {
      setIsAutoLoggingIn(false);
    }
  };

  /**
   * Fast-resume directly into the main app using saved session + preferences.
   *
   * - Reads OS permission state silently (no popup — they are already granted at OS level).
   * - Works even when saved preferences are missing (e.g. fresh APK install over an existing
   *   session, or return from the system camera before prefs were ever written).
   *   In that case sensible defaults are used AND written so the next resume is instant.
   * - Sets ALL React state before un-hiding the app so there is never a blank flash.
   */
  const tryFastResume = async (latestUserData: { username: string; fullName: string; mobileNumber: string }): Promise<boolean> => {
    try {
      const prefs = getSavedPreferences();

      // Use saved voice gender or fall back to 'male'; never block on missing prefs
      const gender: 'male' | 'female' = prefs?.voiceGender ?? 'male';

      // Silent permission query — does NOT show any permission dialog
      const currentPerms = await getAllPermissionsStatus();

      // Persist defaults so the next fast-resume is fully pre-populated
      if (!prefs) {
        savePreferences({ voiceGender: gender });
        console.log('⚡ [App] Saved default preferences for future fast-resumes');
      }

      // Set ALL state before revealing the main app — React 18 batches into one render
      setPermissions(currentPerms);
      setVoiceGender(gender);
      setUserData(latestUserData);
      setShowSplash(false);
      setShowLogin(false);
      setShowRegister(false);
      setShowWelcome(false);
      console.log('⚡ [App] Fast-resume → main app (skipping welcome screen)');
      return true;
    } catch (err) {
      console.error('⚠️ [App] Fast-resume failed:', err);
      return false;
    }
  };

  // Handle splash screen completion
  const handleSplashComplete = async () => {
    // NOTE: do NOT call setShowSplash(false) here — each branch below decides
    // when to reveal the next screen so there is never a blank intermediate state.

    // ── Case 1: Valid session in localStorage ──────────────────────────────────
    if (isAuthenticated()) {
      const savedUserData = getUserData();
      if (savedUserData) {
        // Always show welcome on each page load (including refresh) so the user
        // goes through voice/permissions/draft flow instead of skipping to main app.
        setUserData(savedUserData);
        setShowWelcome(true);
        setShowSplash(false);
        return;
      }
    }

    // ── Case 2: No session → try silent credential-based auto-login ───────────
    const autoLoginSuccess = await tryAutoLogin();
    if (autoLoginSuccess) {
      const savedUserData = getUserData();
      if (savedUserData) {
        setShowWelcome(true);
        setShowSplash(false);
        return;
      }
      setShowWelcome(true);
      setShowSplash(false);
    } else {
      // No session, no saved credentials → must login manually
      clearAllAuth();
      setUserData(null);
      setShowLogin(true);
      setShowSplash(false);
    }
  };

  /**
   * Foreground-return handler (visibilitychange).
   *
   * Scenario: User opens the system camera (or any other app) and comes back.
   * - If the process was NOT killed → React state is intact → session is fine, do nothing.
   * - If the process WAS killed  → fresh React start → handleSplashComplete already ran
   *   the fast-resume path, so by the time any visibilitychange fires we are in the main app.
   *
   * The only edge case we must guard against here is the app being in the main-app view while
   * localStorage somehow lost the session. In that case we silently fast-resume (no API call)
   * or, if we cannot, show the login screen without touching credentials.
   */
  useEffect(() => {
    const onForeground = async () => {
      if (document.visibilityState !== 'visible') return;
      // Only act when the user is supposed to be in an authenticated view
      const inMainView = !showSplash && !showLogin && !showRegister && !showWelcome && !isAutoLoggingIn;
      if (!inMainView) return;

      if (isAuthenticated()) return; // session still healthy — nothing to do

      // Session is gone but we're in the main view → try to recover without an API call first
      const saved = getUserData();
      if (saved) {
        const resumed = await tryFastResume(saved);
        if (resumed) return;
      }

      // Cannot recover silently — show login (but don't wipe credentials so auto-login works next time)
      console.warn('⚠️ [App] Session gone on foreground and could not fast-resume, showing login');
      setShowWelcome(false);
      setShowLogin(true);
    };

    document.addEventListener('visibilitychange', onForeground);
    return () => document.removeEventListener('visibilitychange', onForeground);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSplash, showLogin, showRegister, showWelcome, isAutoLoggingIn]);

  // Handle login completion
  const handleLoginComplete = (loginData: { username: string; fullName: string; mobileNumber: string }) => {
    setUserData(loginData);
    setShowLogin(false);
    setShowWelcome(true);
  };

  // Handle register completion (auto-login after registration)
  const handleRegisterComplete = (userData: { username: string; fullName: string; mobileNumber: string }) => {
    setUserData(userData);
    setShowRegister(false);
    setShowWelcome(true);
  };

  // Navigate from login to register
  const handleGoToRegister = () => {
    setShowLogin(false);
    setRegisterScreenKey((k) => k + 1);
    setShowRegister(true);
  };

  // Navigate from register back to login
  const handleGoToLogin = () => {
    setShowRegister(false);
    setShowLogin(true);
  };

  // Handle welcome message completion with permissions
  const handleWelcomeComplete = (grantedPermissions: AllPermissions, selectedVoiceGender: 'male' | 'female', coordinates?: { lat: number; long: number }, draft?: DraftProfile) => {
    console.log(`📋 [APP] Welcome complete - Selected voice: ${selectedVoiceGender}${draft ? `, resuming draft: ${draft.flowType}/${draft.currentAgentId}` : ''}`);
    setPermissions(grantedPermissions);
    setVoiceGender(selectedVoiceGender);

    // Persist preferences so future app restarts can skip this welcome screen
    savePreferences({ voiceGender: selectedVoiceGender });

    if (coordinates) {
      console.log(`📍 [APP] User location captured: ${coordinates.lat}, ${coordinates.long}`);
      setUserLocation({ lat: coordinates.lat, lng: coordinates.long });
    } else {
      console.warn(`⚠️ [APP] No coordinates received from welcome screen`);
    }

    // Store draft for resume (if any)
    if (draft) {
      setResumeDraft(draft);
    }

    console.log(`🎤 [APP] Voice gender selected: ${selectedVoiceGender}`);
    setShowWelcome(false);
    // Go straight to main app; opening splash is only shown once at cold start
    if (draft) {
      console.log(`⏩ [APP] Resuming draft — main app`);
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearAllAuth();
    setUserData(null);
    setPermissions(null);
    setShowWelcome(false);
    setShowRegister(false);
    setShowLogin(true);
  };

  const handleNavigateHome = () => {
    setShowWelcome(true);
    setResumeDraft(undefined);
  };

  return (
    <ThemeProvider>
      <div className="App" style={{
        // Prevent white flash between splashes
        backgroundColor: '#000000',
        minHeight: '100vh'
      }}>
        {(() => {
          console.log(`🎭 [APP] Render state:`, {
            showSplash,
            showLogin,
            showRegister,
            showWelcome,
            voiceGender,
            shouldShowMainApp: !showSplash && !showLogin && !showRegister && !showWelcome
          });
          return null;
        })()}

        <GlobalApiLoadingOverlay />

        {/* Initial Splash Screen (default splash.png) */}
        {showSplash && (
          <SplashScreen key="default-splash" onComplete={handleSplashComplete} gender={null} />
        )}

        {/* Login Screen */}
        {showLogin && (
          <LoginScreen
            onComplete={handleLoginComplete}
            onGoToRegister={handleGoToRegister}
          />
        )}

        {/* Register Screen */}
        {showRegister && (
          <RegisterScreen
            key={registerScreenKey}
            onComplete={handleRegisterComplete}
            onGoToLogin={handleGoToLogin}
          />
        )}

        {/* Welcome Message */}
        {showWelcome && (
          <WelcomeMessage onComplete={handleWelcomeComplete} onLogout={handleLogout} />
        )}

        {/* Only show AgentProvider and MainApp after opening splash and welcome/login are complete */}
        {!showSplash && !showLogin && !showRegister && !showWelcome && (
          <AgentProvider
            key={userData?.username || 'no-user'}
            voiceGender={voiceGender}
            userLocation={userLocation}
            userData={userData}
            resumeDraft={resumeDraft}
          >
            {(() => {
              console.log("🚀 [APP] AgentProvider initialized with user:", userData?.username || 'no-user', 'voice:', voiceGender);
              return null;
            })()}
            <MainApp
              userData={userData}
              permissions={permissions}
              voiceGender={voiceGender}
              resumeDraft={resumeDraft}
              onNavigateHome={handleNavigateHome}
            />
          </AgentProvider>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
